import { View, Text, Pressable, StyleSheet } from 'react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';

// One bordered track, an inset filled segment for the active option — used
// for the Line/Point/Area draw-kind toggle, the Shared/Private visibility
// toggle, and Create's visibility picker so all three read as the same
// control language. Extracted verbatim from DefensePlanPanel.web.tsx (was
// private there); moved off the .web path (Sept 29 2026) since it only ever
// used plain RN primitives — Create needs it on native too.
export interface SegmentOption<T extends string> {
  key: T;
  label: string;
  icon?: LucideIcon;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  testID,
  // Defaults to the tab/tablist semantics the draw-kind and plan-visibility
  // toggles already shipped with. Create's "Who can see this?" picker was a
  // radiogroup of pills before this migration (Sept 29 2026) — an
  // interactions audit asserts `[role="radio"][aria-checked="true"]` there,
  // so it opts into 'radiogroup' to keep reading the same way.
  role = 'tablist',
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (key: T) => void;
  ariaLabel: string;
  testID?: string;
  role?: 'tablist' | 'radiogroup';
}) {
  const { colors } = useTheme();
  return (
    <View
      style={[styles.segmented, { borderColor: colors.border }]}
      accessibilityRole={role}
      accessibilityLabel={ariaLabel}
      testID={testID}>
      {options.map((option) => (
        <SegmentedOption
          key={option.key}
          option={option}
          selected={option.key === value}
          onPress={() => onChange(option.key)}
          testID={testID ? `${testID}-${option.key}` : undefined}
          role={role === 'radiogroup' ? 'radio' : 'tab'}
        />
      ))}
    </View>
  );
}

export function SegmentedOption<T extends string>({
  option,
  selected,
  onPress,
  testID,
  role = 'tab',
}: {
  option: SegmentOption<T>;
  selected: boolean;
  onPress: () => void;
  testID?: string;
  role?: 'tab' | 'radio';
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const Icon = option.icon;
  const tinted = selected || hovered;
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole={role}
      {...(role === 'radio' ? { 'aria-checked': selected } : { 'aria-selected': selected })}
      accessibilityLabel={option.label}
      {...hoverProps}
      style={[
        styles.segment,
        CURSOR_POINTER,
        { backgroundColor: tinted ? colors.hoverSurface : 'transparent' },
      ]}>
      {Icon && <Icon size={14} color={selected ? colors.text : colors.textSecondary} />}
      <Text
        style={[
          styles.segmentLabel,
          { color: selected ? colors.text : colors.textSecondary },
          selected && styles.segmentLabelSelected,
        ]}>
        {option.label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  segmented: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    padding: 2,
    gap: 2,
  },
  segment: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    // Icon to label, as on Button. Only options with an icon have two
    // children, so segments without one don't move (Sept 29 2026).
    gap: SPACING.xs + SPACING.xxs,
    paddingVertical: SPACING.xs,
    borderRadius: BORDER_RADIUS.sm,
  },
  segmentLabel: {
    ...TYPOGRAPHY.label,
  },
  segmentLabelSelected: {
    ...TYPOGRAPHY.chip, // semibold when selected
  },
});
